import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Separator } from './ui/separator';
import { Textarea } from './ui/textarea';
import {
  DollarSign, Plus, ArrowDownRight, ArrowUpRight,
  Loader2, Zap, FileText, Clock
} from 'lucide-react';
import { toast } from 'sonner';

export default function CustomerCredits({ customerId, customerName, invoices }) {
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [creditForm, setCreditForm] = useState({
    amount: '', credit_type: 'manual', description: '',
    restricted_to_product_names: [],
    monthly_limit: '',
    expires_at: '',
  });
  const [restrictionMode, setRestrictionMode] = useState('none'); // none, products, monthly, both
  const [productInput, setProductInput] = useState('');

  const [applyForm, setApplyForm] = useState({
    invoice_id: '', amount: ''
  });

  const loadCredits = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/customers/${customerId}/credits`);
      setBalance(res.data.available_credit || 0);
      setTransactions(res.data.transactions || []);
      setTotal(res.data.total || 0);
    } catch {
      /* ignore */
    }
    setLoading(false);
  }, [customerId]);

  useEffect(() => { loadCredits(); }, [loadCredits]);

  const handleAddCredit = async (e) => {
    e.preventDefault();
    if (!creditForm.amount || parseFloat(creditForm.amount) <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        amount: parseFloat(creditForm.amount),
        credit_type: creditForm.credit_type,
        description: creditForm.description,
      };
      if (creditForm.restricted_to_product_names.length > 0) {
        payload.restricted_to_product_names = creditForm.restricted_to_product_names;
      }
      if (creditForm.monthly_limit && parseFloat(creditForm.monthly_limit) > 0) {
        payload.monthly_limit = parseFloat(creditForm.monthly_limit);
      }
      if (creditForm.expires_at) {
        payload.expires_at = creditForm.expires_at;
      }
      const res = await api.post(`/customers/${customerId}/credits`, payload);
      toast.success(res.data.message);
      setBalance(res.data.available_credit);
      setCreditForm({ amount: '', credit_type: 'manual', description: '', restricted_to_product_names: [], monthly_limit: '', expires_at: '' });
      setRestrictionMode('none');
      setProductInput('');
      setAddOpen(false);
      loadCredits();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add credit');
    }
    setSubmitting(false);
  };

  const handleApplyCredit = async (e) => {
    e.preventDefault();
    if (!applyForm.invoice_id) { toast.error('Select an invoice'); return; }
    setSubmitting(true);
    try {
      const payload = { invoice_id: applyForm.invoice_id };
      if (applyForm.amount) payload.amount = parseFloat(applyForm.amount);
      const res = await api.post(`/customers/${customerId}/credits/apply`, payload);
      toast.success(res.data.message);
      setBalance(res.data.available_credit);
      setApplyForm({ invoice_id: '', amount: '' });
      setApplyOpen(false);
      loadCredits();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply credit');
    }
    setSubmitting(false);
  };

  const handleAutoApply = async () => {
    setSubmitting(true);
    try {
      const res = await api.post(`/customers/${customerId}/credits/auto-apply`, {});
      if (res.data.applied_count > 0) {
        toast.success(res.data.message);
      } else {
        toast.info('No unpaid invoices to apply credit to');
      }
      setBalance(res.data.available_credit);
      loadCredits();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to auto-apply');
    }
    setSubmitting(false);
  };

  const handleRemoveAppliedCredit = async (transactionId) => {
    if (!window.confirm('Remove this applied credit? The amount will be returned to the customer\'s credit balance and the invoice status may revert.')) return;
    try {
      const res = await api.post(`/customers/${customerId}/credits/remove-applied`, { transaction_id: transactionId });
      toast.success(res.data.message);
      setBalance(res.data.available_credit);
      loadCredits();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove credit');
    }
  };

  const unpaidInvoices = (invoices || []).filter(i => !['paid', 'cancelled'].includes(i.status));

  const typeLabel = (t) => ({
    manual: 'Manual',
    overpayment: 'Overpayment',
    credit_note: 'Credit Note',
    return: 'Return',
    applied_to_invoice: 'Applied',
    auto_applied: 'Auto-Applied',
  })[t] || t;

  const typeColor = (t) => ({
    manual: 'bg-blue-100 text-blue-700',
    overpayment: 'bg-purple-100 text-purple-700',
    credit_note: 'bg-amber-100 text-amber-700',
    return: 'bg-teal-100 text-teal-700',
    applied_to_invoice: 'bg-gray-100 text-gray-600',
    auto_applied: 'bg-gray-100 text-gray-600',
  })[t] || 'bg-gray-100 text-gray-600';

  return (
    <div className="space-y-4" data-testid="customer-credits-tab">
      {/* Balance Card */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Card className="flex-1">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-600 flex items-center justify-center ring-1 ring-emerald-400/40">
              <DollarSign className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Available Credit</p>
              <p className="text-2xl font-bold text-emerald-500" data-testid="credit-balance">
                ${balance.toFixed(2)}
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center gap-2 flex-shrink-0">
          <Button onClick={() => setAddOpen(true)} data-testid="add-credit-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Add Credit
          </Button>
          {balance > 0 && (
            <>
              <Button variant="outline" onClick={() => setApplyOpen(true)} data-testid="apply-credit-btn">
                <ArrowDownRight className="h-4 w-4 mr-1.5" /> Apply to Invoice
              </Button>
              <Button variant="outline" onClick={handleAutoApply} disabled={submitting} data-testid="auto-apply-btn">
                <Zap className="h-4 w-4 mr-1.5" /> Auto-Apply
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Transaction History */}
      <Card>
        <CardHeader className="pb-2 px-4 pt-4">
          <CardTitle className="text-sm">Credit History ({total})</CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading...
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              No credit transactions yet
            </div>
          ) : (
            <div className="space-y-1" data-testid="credit-transactions">
              {transactions.map((txn) => (
                <div key={txn.id} className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-gray-50 border-b last:border-0">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      txn.direction === 'credit' ? 'bg-emerald-50' : 'bg-gray-50'
                    }`}>
                      {txn.direction === 'credit'
                        ? <ArrowDownRight className="h-4 w-4 text-emerald-600" />
                        : <ArrowUpRight className="h-4 w-4 text-gray-500" />
                      }
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium truncate">{txn.description}</span>
                        <Badge className={`text-[10px] ${typeColor(txn.credit_type)}`}>
                          {typeLabel(txn.credit_type)}
                        </Badge>
                        {txn.restricted_to_product_names?.length > 0 && (
                          <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">Products only</Badge>
                        )}
                        {txn.monthly_limit && (
                          <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-700">${txn.monthly_limit}/mo</Badge>
                        )}
                        {txn.expires_at && (
                          <Badge variant="outline" className="text-[10px] border-red-300 text-red-700">Exp {new Date(txn.expires_at).toLocaleDateString()}</Badge>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                        <Clock className="h-3 w-3" />
                        {new Date(txn.created_at).toLocaleString()}
                        {txn.applied_to_invoice_id && (
                          <span className="flex items-center gap-1">
                            <FileText className="h-3 w-3" /> Invoice applied
                          </span>
                        )}
                        {txn.created_by && <span>by {txn.created_by}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`font-mono font-semibold text-sm ${
                      txn.direction === 'credit' ? 'text-emerald-600' : 'text-gray-600'
                    }`}>
                      {txn.direction === 'credit' ? '+' : '-'}${txn.amount.toFixed(2)}
                    </span>
                    {txn.direction === 'debit' && txn.applied_to_invoice_id && (
                      <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[10px] text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleRemoveAppliedCredit(txn.id)} data-testid={`remove-credit-${txn.id}`}>
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Credit Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md" data-testid="add-credit-dialog">
          <DialogHeader>
            <DialogTitle>Add Credit — {customerName}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddCredit} className="space-y-4">
            <div>
              <Label>Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={creditForm.amount}
                onChange={(e) => setCreditForm(p => ({ ...p, amount: e.target.value }))}
                placeholder="0.00"
                required
                data-testid="credit-amount-input"
              />
            </div>
            <div>
              <Label>Credit Type</Label>
              <Select value={creditForm.credit_type} onValueChange={(v) => setCreditForm(p => ({ ...p, credit_type: v }))}>
                <SelectTrigger data-testid="credit-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual Credit</SelectItem>
                  <SelectItem value="overpayment">Overpayment</SelectItem>
                  <SelectItem value="credit_note">Credit Note</SelectItem>
                  <SelectItem value="return">Return / Refund</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={creditForm.description}
                onChange={(e) => setCreditForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Reason for the credit..."
                rows={2}
                data-testid="credit-description"
              />
            </div>

            {/* Credit Restrictions */}
            <div className="border rounded-lg p-3 bg-gray-50 space-y-3">
              <Label className="text-sm font-semibold">Usage Restrictions (optional)</Label>
              
              {/* Product/Service restriction */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <input
                    type="checkbox"
                    checked={creditForm.restricted_to_product_names.length > 0 || restrictionMode === 'products' || restrictionMode === 'both'}
                    onChange={(e) => {
                      if (!e.target.checked) {
                        setCreditForm(p => ({ ...p, restricted_to_product_names: [] }));
                        setRestrictionMode(restrictionMode === 'both' ? 'monthly' : 'none');
                      } else {
                        setRestrictionMode(restrictionMode === 'monthly' ? 'both' : 'products');
                      }
                    }}
                    className="rounded"
                    data-testid="restrict-products-checkbox"
                  />
                  <Label className="text-xs">Limit to specific products/services</Label>
                </div>
                {(creditForm.restricted_to_product_names.length > 0 || restrictionMode === 'products' || restrictionMode === 'both') && (
                  <div className="ml-5 space-y-1">
                    <div className="flex gap-1 flex-wrap">
                      {creditForm.restricted_to_product_names.map((name, i) => (
                        <Badge key={i} variant="secondary" className="text-xs cursor-pointer" onClick={() => setCreditForm(p => ({ ...p, restricted_to_product_names: p.restricted_to_product_names.filter((_, j) => j !== i) }))}>
                          {name} ×
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-1">
                      <Input
                        value={productInput}
                        onChange={(e) => setProductInput(e.target.value)}
                        placeholder="Type product/service name, press Enter"
                        className="h-7 text-xs"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const v = productInput.trim();
                            if (v && !creditForm.restricted_to_product_names.includes(v)) {
                              setCreditForm(p => ({ ...p, restricted_to_product_names: [...p.restricted_to_product_names, v] }));
                              setProductInput('');
                            }
                          }
                        }}
                        data-testid="restrict-product-input"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Monthly limit */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <input
                    type="checkbox"
                    checked={!!creditForm.monthly_limit || restrictionMode === 'monthly' || restrictionMode === 'both'}
                    onChange={(e) => {
                      if (!e.target.checked) {
                        setCreditForm(p => ({ ...p, monthly_limit: '' }));
                        setRestrictionMode(restrictionMode === 'both' ? 'products' : 'none');
                      } else {
                        setRestrictionMode(restrictionMode === 'products' ? 'both' : 'monthly');
                      }
                    }}
                    className="rounded"
                    data-testid="restrict-monthly-checkbox"
                  />
                  <Label className="text-xs">Limit amount applied per month</Label>
                </div>
                {(!!creditForm.monthly_limit || restrictionMode === 'monthly' || restrictionMode === 'both') && (
                  <div className="ml-5">
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={creditForm.monthly_limit}
                      onChange={(e) => setCreditForm(p => ({ ...p, monthly_limit: e.target.value }))}
                      placeholder="Max $ per month"
                      className="h-7 text-xs w-40"
                      data-testid="restrict-monthly-input"
                    />
                  </div>
                )}
              </div>

              {/* Expiration date */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <input
                    type="checkbox"
                    checked={!!creditForm.expires_at}
                    onChange={(e) => {
                      if (!e.target.checked) setCreditForm(p => ({ ...p, expires_at: '' }));
                    }}
                    className="rounded"
                    data-testid="restrict-expiry-checkbox"
                  />
                  <Label className="text-xs">Set expiration date</Label>
                </div>
                {!!creditForm.expires_at || (
                  <span></span>
                )}
                <div className="ml-5">
                  <Input
                    type="date"
                    value={creditForm.expires_at}
                    onChange={(e) => setCreditForm(p => ({ ...p, expires_at: e.target.value }))}
                    className="h-7 text-xs w-40"
                    data-testid="restrict-expiry-input"
                  />
                </div>
              </div>
            </div>
            <Button type="submit" disabled={submitting} className="w-full" data-testid="credit-submit-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Add ${creditForm.amount || '0.00'} Credit
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Apply Credit Dialog */}
      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent className="max-w-md" data-testid="apply-credit-dialog">
          <DialogHeader>
            <DialogTitle>Apply Credit to Invoice</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-gray-600 mb-2">
            Available: <strong className="text-emerald-700">${balance.toFixed(2)}</strong>
          </div>
          <form onSubmit={handleApplyCredit} className="space-y-4">
            <div>
              <Label>Select Invoice</Label>
              <Select value={applyForm.invoice_id || undefined} onValueChange={(v) => setApplyForm(p => ({ ...p, invoice_id: v }))}>
                <SelectTrigger data-testid="apply-invoice-select"><SelectValue placeholder="Choose an unpaid invoice..." /></SelectTrigger>
                <SelectContent>
                  {unpaidInvoices.length === 0 ? (
                    <SelectItem value="__none" disabled>No unpaid invoices</SelectItem>
                  ) : (
                    unpaidInvoices.map(inv => (
                      <SelectItem key={inv.id} value={inv.id}>
                        {inv.invoice_number} — ${Number(inv.total || 0).toFixed(2)} ({inv.status})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount to Apply *</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                max={balance}
                value={applyForm.amount}
                onChange={(e) => setApplyForm(p => ({ ...p, amount: e.target.value }))}
                placeholder="Enter amount"
                required
                data-testid="apply-amount-input"
              />
              <p className="text-xs text-gray-500 mt-1">Available: ${balance.toFixed(2)} · Invoice balance: ${(() => { const inv = unpaidInvoices.find(i => i.id === applyForm.invoice_id); return inv ? (Number(inv.total || 0) - Number(inv.credits_applied || 0)).toFixed(2) : '—'; })()}</p>
            </div>
            <Button type="submit" disabled={submitting || !applyForm.invoice_id || !applyForm.amount} className="w-full" data-testid="apply-submit-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowDownRight className="h-4 w-4 mr-2" />}
              Apply Credit
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
